import { useState } from 'react'

interface CardContentProps {
  content: string
  showTranslations?: boolean
  onToggleTranslations?: () => void
  /** If true, renders TTS buttons that call window.speakText(). Requires global speakText function. */
  enableTTSButtons?: boolean
}

interface ProcessedContent {
  content: string
  translations: string[]
}

/**
 * Process card content to extract translations and format text.
 *
 * Syntax supported:
 * - $text$ - Text-to-speech (renders as button if enableTTSButtons, otherwise plain text)
 * - %text% - Translation (hidden by default, shown via toggle)
 * - **text** - Bold
 * - *text* - Italic
 *
 * Note: Content is from admin-managed cards, not user input.
 */
export function processCardContent(content: string, enableTTSButtons: boolean = true): ProcessedContent {
  const translations: string[] = []
  let processedContent = content

  // Remove % translations and collect them
  const translationMatches = content.match(/%([^%]+)%/g) || []
  translationMatches.forEach((match) => {
    const innerText = match.slice(1, -1)
    translations.push(innerText)
    processedContent = processedContent.replace(match, '')
  })

  // Handle text-to-speech
  const ttsMatches = processedContent.match(/\$([^$]+)\$/g) || []
  ttsMatches.forEach((match) => {
    const innerText = match.slice(1, -1)
    if (enableTTSButtons) {
      // Replace underscores with "blanco" for speech
      const spokenText = innerText.replace(/_+/g, 'blanco')
      processedContent = processedContent.replace(
        match,
        `<button onclick="window.speakText('${spokenText.replace(/'/g, "\\'")}')" class="inline-flex items-center px-3 py-1 rounded text-base font-medium bg-green-100 text-green-700 hover:bg-green-200">🔊 ${innerText}</button>`
      )
    } else {
      // Just display the text without TTS button
      processedContent = processedContent.replace(match, `<span class="tts-text">${innerText}</span>`)
    }
  })

  // Basic markdown processing
  // Bold (must come before italic to handle **text** correctly)
  processedContent = processedContent.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  // Italic
  processedContent = processedContent.replace(/\*([^*]+)\*/g, '<em>$1</em>')

  return { content: processedContent, translations }
}

/**
 * Renders processed card content with optional translation toggle.
 *
 * When used with TTS buttons, ensure window.speakText is defined globally
 * (typically set up in the parent component's useEffect).
 *
 * Note: Uses innerHTML for admin-managed card content rendering.
 */
export function CardContent({
  content,
  showTranslations: controlledShowTranslations,
  onToggleTranslations,
  enableTTSButtons = true,
}: CardContentProps) {
  // Support both controlled and uncontrolled translation state
  const [internalShowTranslations, setInternalShowTranslations] = useState(false)

  const showTranslations = controlledShowTranslations ?? internalShowTranslations
  const toggleTranslations = onToggleTranslations ?? (() => setInternalShowTranslations(!internalShowTranslations))

  const { content: processedContent, translations } = processCardContent(content, enableTTSButtons)

  return (
    <div className="space-y-4">
      <div
        className="prose prose-sm max-w-none whitespace-pre-wrap"
        dangerouslySetInnerHTML={{ __html: processedContent }}
      />

      {translations.length > 0 && (
        <div className="border-t pt-4">
          <button
            onClick={toggleTranslations}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
              />
            </svg>
            {showTranslations ? 'Hide' : 'Show'} Translations
          </button>

          {showTranslations && (
            <div className="mt-2 text-sm text-gray-600 space-y-1">
              {translations.map((translation, index) => (
                <div key={index}>• {translation}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
